import type { UseQueryResult } from '@tanstack/react-query';

import type { ErroApi } from '@/nucleo/auth/cliente-api';
import { paginar, type Pagina } from '@/modulos/portal-credenciado/comum/contrato';
import {
  documentosDoChecklist,
  textoOuNulo,
  useRecursosDoCredenciado,
  validadeDoRequisito,
  type RecursoDaApi,
} from '@/modulos/portal-credenciado/comum/recursos';

import type { FiltroDeVeiculos, Veiculo } from './contrato';

/**
 * Camada de dados da frota do credenciado: `comum/recursos.ts`, tipo
 * `veiculo`. Nenhum componente busca dado direto.
 */

/** A linha da API no formato da tela. O que a API não devolve fica `null`. */
export function paraOVeiculo(recurso: RecursoDaApi): Veiculo {
  return {
    id: recurso.id,
    placa: recurso.identificador,
    marca_modelo: textoOuNulo(recurso.nome),
    situacao: recurso.estado,
    documentos: documentosDoChecklist(recurso.documentos),
    crm: null,
    cor: null,
    ano_fabricacao: null,
    ano_modelo: null,
    chassi: null,
    renavam: null,
    tipo_combustivel: null,
    acessivel: null,
    capacidade_detran: null,
    capacidade_teg: null,
    capacidade_total: null,
    capacidade_total_origem: null,
    capacidade_sgtp_snapshot: null,
    ultima_consulta_sgtp_capacidade: null,
    capacidade_creche: null,
    capacidade_ancoragens_pcd: null,
    valor_mensal: null,
    crlv_validade: validadeDoRequisito(recurso.documentos, 'CRLV'),
    seguro_seguradora: null,
    seguro_apolice: null,
    seguro_validade: validadeDoRequisito(recurso.documentos, 'APOLICE_SEGURO'),
    seguro_app_valor: null,
    seguro_app_vencimento: validadeDoRequisito(recurso.documentos, 'APOLICE_SEGURO'),
    seguro_rc_valor: null,
    seguro_rc_vencimento: validadeDoRequisito(recurso.documentos, 'APOLICE_SEGURO'),
    vistoria_validade: validadeDoRequisito(recurso.documentos, 'VISTORIA_SEMESTRAL_DETRAN'),
    observacoes: null,
  };
}

/** Busca em placa, marca e modelo; situação exata. */
function filtrarVeiculos(veiculos: readonly Veiculo[], filtro: FiltroDeVeiculos): Veiculo[] {
  const busca = filtro.busca?.trim().toLowerCase();

  return veiculos.filter((veiculo) => {
    if (filtro.situacao && veiculo.situacao !== filtro.situacao) {
      return false;
    }
    if (busca) {
      const alvo = `${veiculo.placa} ${veiculo.marca_modelo ?? ''}`.toLowerCase();
      if (!alvo.includes(busca)) {
        return false;
      }
    }
    return true;
  });
}

/** Ordenado por placa, como a consulta do protótipo (`.order("placa")`). */
function ordenarPorPlaca(veiculos: Veiculo[]): Veiculo[] {
  return [...veiculos].sort((a, b) => a.placa.localeCompare(b.placa));
}

export function useVeiculos(
  filtro: FiltroDeVeiculos,
  pagina: number,
  tamanho: number,
): UseQueryResult<Pagina<Veiculo>, ErroApi> {
  return useRecursosDoCredenciado('veiculo', paraOVeiculo, (veiculos) =>
    paginar(ordenarPorPlaca(filtrarVeiculos(veiculos, filtro)), pagina, tamanho),
  );
}

/**
 * `null` quando o id não é da frota: o React Query recusa `undefined` como
 * dado e trataria o "não encontrado" como falha de carregamento.
 */
export function useVeiculo(id: string | undefined): UseQueryResult<Veiculo | null, ErroApi> {
  return useRecursosDoCredenciado(
    'veiculo',
    paraOVeiculo,
    (veiculos) => veiculos.find((veiculo) => veiculo.id === id) ?? null,
    !!id,
  );
}
